import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface BoardFilterOption {
    id: string;
    label: string;
    color?: string;
    meta?: string;
    /** Optional avatar image (e.g. social account profile picture). */
    iconUrl?: string | null;
    /** Optional platform key for a brand-icon fallback when iconUrl is missing. */
    platform?: string;
}

interface BoardFilterDropdownProps {
    label: string;
    options: BoardFilterOption[];
    /** Currently applied ids. */
    selected: string[];
    onChange: (next: string[]) => void;
    /** 'multi' → checkbox list + Clear/Apply footer; 'single' → commit on click. */
    mode?: 'multi' | 'single';
    /**
     * When false, hide the leading checkbox square (used for single-select
     * Date ranges — select one row, show a trailing check instead).
     * Defaults to true so CRM multi-filters keep their checkbox UI.
     */
    showCheckbox?: boolean;
    searchable?: boolean;
    searchPlaceholder?: string;
    align?: 'start' | 'end';
    /** Message when there are no options at all (e.g. no agents yet). */
    emptyText?: string;
    /**
     * Footer reset label. Defaults to 'Clear' so every existing consumer is
     * byte-identical; commerce-v2 passes 'Clear all' to match the reference
     * design, where this button is the only reset (that toolbar has no
     * separate page-level Clear link).
     */
    clearLabel?: string;
    loading?: boolean;
    className?: string;
}

/**
 * CRM filter-bar pill dropdown. Visual match for the wenext-onboarding
 * `FilterDropdown` reference (green active state, checkbox rows, search,
 * Clear/Apply footer) built on the shared shadcn Popover so outside-click,
 * portalling and positioning are handled for us — no manual listeners.
 *
 * `multi` mode uses a draft that only commits on Apply (so the board doesn't
 * refetch on every checkbox tick). `single` mode commits immediately on click.
 */
export function BoardFilterDropdown({
    label,
    options,
    selected,
    onChange,
    mode = 'multi',
    showCheckbox = true,
    searchable = true,
    searchPlaceholder,
    align = 'start',
    emptyText = 'No options',
    clearLabel = 'Clear',
    loading = false,
    className,
}: BoardFilterDropdownProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [draft, setDraft] = useState<string[]>(selected);

    // Re-sync the draft to the applied value whenever the popover (re)opens or
    // the applied selection changes underneath us (e.g. Clear all).
    useEffect(() => {
        if (open) setDraft(selected);
    }, [open, selected]);

    useEffect(() => {
        if (!open) setQuery('');
    }, [open]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    const activeCount = selected.length;

    const commit = (next: string[]) => {
        onChange(next);
        setOpen(false);
    };

    const toggleDraft = (id: string) =>
        setDraft((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const handleRowClick = (id: string) => {
        if (mode === 'single') {
            // Toggle-off if the same single value is clicked again.
            commit(selected.includes(id) ? [] : [id]);
        } else {
            toggleDraft(id);
        }
    };

    const showApply = mode === 'multi';
    const canClear = mode === 'single' ? activeCount > 0 : draft.length > 0 || activeCount > 0;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'flex items-center gap-1.5 rounded-lg border px-2.5 h-9 text-[12.5px] font-medium transition shrink-0',
                        activeCount > 0
                            ? 'border-[#0EB64F] bg-[#F4FBF6] text-[#076A2D]'
                            : 'border-[#E4E7EC] bg-white text-[#344054] hover:bg-[#F9FAFB]',
                        className,
                    )}
                >
                    {label}
                    {activeCount > 0 && (
                        <span className="inline-flex items-center justify-center rounded-full bg-[#0EB64F] text-white text-[10px] font-semibold min-w-[16px] h-4 px-1">
                            {activeCount}
                        </span>
                    )}
                    <ChevronDown
                        className={cn('w-3.5 h-3.5 text-[#98A2B3] transition-transform', open && 'rotate-180')}
                    />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align={align}
                sideOffset={6}
                className="w-[260px] p-0 rounded-xl bg-white border border-[#E4E7EC] shadow-[0_8px_20px_rgba(16,24,40,0.08)]"
            >
                {searchable && options.length > 6 && (
                    <div className="p-2.5 border-b border-[#F2F4F7]">
                        <div className="flex items-center gap-2 rounded-lg border border-[#E4E7EC] bg-white px-2.5 py-1.5">
                            <Search className="w-3.5 h-3.5 text-[#98A2B3] shrink-0" />
                            <input
                                autoFocus
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={searchPlaceholder || `Search ${label.toLowerCase()}…`}
                                className="flex-1 bg-transparent outline-none text-[12.5px] text-[#101828] placeholder:text-[#98A2B3] min-w-0"
                            />
                        </div>
                    </div>
                )}

                <div className="max-h-[280px] overflow-y-auto p-1.5">
                    {loading ? (
                        <p className="px-3 py-3 text-[12px] text-[#98A2B3]">Loading…</p>
                    ) : options.length === 0 ? (
                        <p className="px-3 py-3 text-[12px] text-[#98A2B3]">{emptyText}</p>
                    ) : filtered.length === 0 ? (
                        <p className="px-3 py-3 text-[12px] text-[#98A2B3]">No matches</p>
                    ) : (
                        filtered.map((o) => {
                            const checked = mode === 'single' ? selected.includes(o.id) : draft.includes(o.id);
                            return (
                                <button
                                    key={o.id}
                                    type="button"
                                    onClick={() => handleRowClick(o.id)}
                                    className={cn(
                                        'w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left transition',
                                        checked ? 'bg-[#F4FBF6]' : 'hover:bg-[#F9FAFB]',
                                    )}
                                >
                                    {showCheckbox ? (
                                        <span
                                            className={cn(
                                                'w-4 h-4 rounded border flex items-center justify-center shrink-0 transition',
                                                checked ? 'bg-[#0EB64F] border-[#0EB64F]' : 'border-[#D0D5DD] bg-white',
                                            )}
                                        >
                                            {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                        </span>
                                    ) : null}
                                    {o.color && (
                                        <span
                                            className="w-2 h-2 rounded-full shrink-0"
                                            style={{ background: o.color }}
                                        />
                                    )}
                                    {o.iconUrl ? (
                                        <img
                                            src={o.iconUrl}
                                            alt=""
                                            className="h-5 w-5 shrink-0 rounded-full object-cover"
                                        />
                                    ) : null}
                                    <span className="flex-1 min-w-0 text-[13px] font-medium text-[#101828] truncate">
                                        {o.label}
                                    </span>
                                    {o.meta && <span className="text-[11px] text-[#98A2B3] shrink-0">{o.meta}</span>}
                                    {!showCheckbox && checked ? (
                                        <Check className="h-3.5 w-3.5 shrink-0 text-[#0EB64F]" strokeWidth={3} />
                                    ) : null}
                                </button>
                            );
                        })
                    )}
                </div>

                {(showApply || canClear) && (
                    <div className="border-t border-[#F2F4F7] p-2 flex items-center justify-between gap-2">
                        <button
                            type="button"
                            onClick={() => commit([])}
                            disabled={!canClear}
                            className={cn(
                                'text-[12.5px] font-semibold px-2.5 h-8 rounded-lg transition',
                                canClear
                                    ? 'text-[#475467] hover:bg-[#F9FAFB]'
                                    : 'text-[#D0D5DD] cursor-not-allowed',
                            )}
                        >
                            {clearLabel}
                        </button>
                        {showApply && (
                            <button
                                type="button"
                                onClick={() => commit(draft)}
                                className="text-[12.5px] font-semibold px-3 h-8 rounded-lg bg-[#0EB64F] hover:bg-[#0A8A3B] text-white transition"
                            >
                                Apply
                            </button>
                        )}
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
